import { useQuery } from "@tanstack/react-query";
import {
  LIVE_LOG_CONTENT_TYPE,
  type APIError,
  type LiveLogContainerCatalog,
  type LiveLogControlFrame,
  type LiveLogFrame,
  type LiveLogLineFrame,
  type LiveLogOptions,
} from "@k8s-dashboard/contracts";
import { HttpError, apiGet, clusterPath, currentScenarioParam, getManagerToken, getSessionCSRF } from "./client";

/**
 * 라이브 Pod 로그 클라이언트 (ADR 0021 · Issue #9)
 * --------------------------------------------------------------------------
 * 브라우저는 데이터소스를 직접 부르지 않습니다. 이 모듈도 BFF 경로 두 개만 씁니다.
 * NDJSON은 fetch + ReadableStream으로 읽습니다 — EventSource는 Authorization
 * 헤더를 붙일 수 없어 인증 경계를 깨뜨립니다.
 */

const base = (clusterId: string, podName: string) =>
  clusterPath(clusterId, "pods", podName, "live-logs");

export const liveLogKeys = {
  containers: (clusterId: string, ns: string, podUid: string) =>
    ["live-logs", "containers", clusterId, ns, podUid] as const,
};

/** container 선택지. Pod UID가 캐시 키입니다 — 이름은 신원이 아닙니다. */
export function useLiveLogContainers(clusterId: string, ns: string, podName: string, podUid: string, enabled: boolean) {
  return useQuery({
    queryKey: liveLogKeys.containers(clusterId, ns, podUid),
    enabled: enabled && Boolean(clusterId && ns && podName && podUid),
    retry: false,
    staleTime: 10_000,
    queryFn: ({ signal }) =>
      apiGet<LiveLogContainerCatalog>(`${base(clusterId, podName)}/containers`, { ns, uid: podUid }, signal),
  });
}

/** 스트림 쿼리 파라미터. 서버가 상한을 강제하므로 UI는 요청만 합니다. */
export function liveLogSearchParams(ns: string, podUid: string, options: LiveLogOptions): URLSearchParams {
  const params = new URLSearchParams({ ns, uid: podUid, container: options.container });
  if (options.tailLines) params.set("tailLines", String(options.tailLines));
  if (options.sinceSeconds) params.set("sinceSeconds", String(options.sinceSeconds));
  if (options.sinceTime) params.set("sinceTime", options.sinceTime);
  if (options.timestamps) params.set("timestamps", "true");
  if (options.previous) params.set("previous", "true");
  if (options.follow) params.set("follow", "true");
  for (const [key, value] of currentScenarioParam()) params.set(key, value);
  return params;
}

/**
 * 인코딩된 프레임 한 줄의 상한(JS 문자 수).
 *
 * 서버의 한 줄 상한은 **원문 64KiB**이지만, 여섯 UTF-8 bullet 마스킹과 JSON
 * 인코딩을 거치면 그보다 길어집니다. 반복되는 짧은 `pwd=x`도 정상적으로 팽창합니다.
 * 제어문자 하나가 `` 6자로 부풀 수 있어 최악 6배이고, 여기에 프레임 봉투
 * (type·seq·timestamp·stream·플래그)가 더 붙습니다. 예전 상한(2×64KiB)은 그 팽창을
 * 감안하지 않아, **정상적인 서버 프레임을 프로토콜 위반으로 거절**할 수 있었습니다.
 *
 * 유한하게 유지하는 것이 목적입니다 — 개행 없이 끝없이 오는 입력은 여전히 끊습니다.
 */
const MAX_ENCODED_FRAME = 6 * 65536 + 4096;

export interface LiveLogHandlers {
  onLine: (frame: LiveLogLineFrame) => void;
  onControl: (frame: LiveLogControlFrame) => void;
}

/**
 * NDJSON 한 줄을 파싱합니다.
 *
 * 깨진 줄이나 모르는 type을 **조용히 버리지 않습니다.** 버리면 잘린 스트림이
 * 정상 종료처럼 보이고, 사용자는 없는 줄을 없다고 믿게 됩니다. 대신 예외를
 * 올려 세션이 error 상태로 끝나게 합니다. 예외 메시지에는 본문을 싣지 않습니다 —
 * 마스킹 이전 값일 수 있습니다.
 */
function dispatch(raw: string, handlers: LiveLogHandlers): void {
  const trimmed = raw.trim();
  if (!trimmed) return;
  let frame: LiveLogFrame;
  try {
    frame = JSON.parse(trimmed) as LiveLogFrame;
  } catch {
    throw new Error("live logs: 스트림에서 해석할 수 없는 프레임을 받았습니다");
  }
  if (frame.type === "line") handlers.onLine(frame);
  else if (frame.type === "control") handlers.onControl(frame);
  else throw new Error("live logs: 알 수 없는 프레임 종류를 받았습니다");
}

/**
 * 스트림을 열고 프레임을 흘려보냅니다.
 *
 * - `signal`이 abort되면 fetch가 끊기고 서버는 취소를 pods/log까지 전파합니다.
 * - 응답 전체를 모으지 않습니다. 청크 경계를 넘는 부분 줄만 유계로 붙잡습니다.
 * - 부분 줄이 고정된 인코딩 상한을 넘으면 스트림을 신뢰하지 않고 끊습니다.
 */
export async function streamLiveLogs(
  clusterId: string,
  podName: string,
  ns: string,
  podUid: string,
  options: LiveLogOptions,
  handlers: LiveLogHandlers,
  signal: AbortSignal,
): Promise<void> {
  const url = `${base(clusterId, podName)}?${liveLogSearchParams(ns, podUid, options)}`;
  const headers = new Headers({ accept: LIVE_LOG_CONTENT_TYPE });
  const token = getManagerToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const csrf = getSessionCSRF();
  if (csrf) headers.set("X-CSRF-Token", csrf);

  const res = await fetch(url, { headers, credentials: "same-origin", signal });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as APIError | null;
    throw new HttpError(res.status, body ?? {
      code: res.status === 403 ? "forbidden" : "upstream_error",
      message: `라이브 로그를 열지 못했습니다 (${res.status})`,
      requestId: res.headers.get("X-Request-ID") ?? "",
    });
  }
  if (!res.body) return;

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let carry = "";
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      carry += decoder.decode(value, { stream: true });
      let newline = carry.indexOf("\n");
      while (newline >= 0) {
        dispatch(carry.slice(0, newline), handlers);
        carry = carry.slice(newline + 1);
        newline = carry.indexOf("\n");
      }
      if (carry.length > MAX_ENCODED_FRAME) {
        // 서버 상한을 넘는 부분 줄은 프로토콜 위반입니다. 계속 모으면 무한 버퍼입니다.
        throw new Error("live logs: unterminated frame exceeded the server line bound");
      }
    }
    dispatch(carry, handlers);
  } finally {
    // reader를 놓아 주면 취소가 네트워크 계층까지 내려갑니다.
    await reader.cancel().catch(() => undefined);
  }
}

/** 마스킹된 줄만으로 다운로드 텍스트를 만듭니다. 원문 경로는 존재하지 않습니다. */
export function maskedDownloadText(lines: LiveLogLineFrame[]): string {
  return lines.map((l) => (l.timestamp ? `${l.timestamp} ${l.maskedLine}` : l.maskedLine)).join("\n");
}
